import { Box, Grid, TextField, Typography, Button } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Organisation } from '../../types/organization';

interface OrganizationFormProps {
  organization: Organisation;
  onSubmit: (data: Partial<Organisation>) => void;
  onCancel: () => void;
}

export default function OrganizationForm({
  organization,
  onSubmit,
  onCancel,
}: OrganizationFormProps) {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      isChatDisabled: organization.isChatDisabled,
      companyName: organization.companyName,
      chatDescription: organization.chatDescription,
      company: organization.company,
      client: organization.client,
      planType: organization.planType,
      isBookingEnabled: organization.isBookingEnabled,
      bookingDescription: organization.bookingDescription,
      clientTableName: organization.clientTableName,
      ingest_status: organization.ingest_status,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={4}>
        {/* Company Information */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 3 }}>
            Company Details
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="company.name"
                control={control}
                rules={{ required: 'Company name is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Company Name"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="company.description"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Description" fullWidth multiline rows={3} />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="company.website"
                control={control}
                render={({ field }) => <TextField {...field} label="Website" fullWidth />}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="company.phone"
                control={control}
                render={({ field }) => <TextField {...field} label="Phone" fullWidth />}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="company.address"
                control={control}
                render={({ field }) => <TextField {...field} label="Address" fullWidth />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="company.city"
                control={control}
                render={({ field }) => <TextField {...field} label="City" fullWidth />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="company.state"
                control={control}
                render={({ field }) => <TextField {...field} label="State" fullWidth />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="company.country"
                control={control}
                render={({ field }) => <TextField {...field} label="Country" fullWidth />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="company.postcode"
                control={control}
                render={({ field }) => <TextField {...field} label="Postcode" fullWidth />}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Client Information */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 3 }}>
            Client Contact
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="client.firstName"
                control={control}
                rules={{ required: 'First name is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="client.lastName"
                control={control}
                rules={{ required: 'Last name is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="client.email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Email"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="client.phone"
                control={control}
                rules={{ required: 'Phone is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Phone"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 4,
        }}
      >
        <Button onClick={onCancel} variant="outlined" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disableElevation>
          Save Changes
        </Button>
      </Box>
    </form>
  );
}
